import { Pencil } from "lucide-react";
import { useEffect } from "react";
import { useImmer } from "use-immer";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack, useTheme } from "@mui/material";
import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";

interface RuleDescriptionEditorProps {
  description: string;
  onSave: (description: string) => Promise<void> | void;
  readOnly?: boolean;
  saving?: boolean;
  /**
   * How the editor sits in its host.
   *
   * - `"strip"` (default) — a tinted, bottom-bordered band directly under a
   *   workspace title bar, which is the only place this lived originally.
   * - `"inline"` — bare text in a normal content flow, for the rule detail
   *   page where the description reads as the rule's summary paragraph rather
   *   than as chrome.
   *
   * The band styling was baked into both branches, so a host that wanted the
   * text without the chrome had no way to ask for it.
   */
  chrome?: "strip" | "inline";
}

export const RuleDescriptionEditor = ({
  description,
  onSave,
  readOnly = false,
  saving = false,
  chrome = "strip",
}: RuleDescriptionEditorProps) => {
  const theme = useTheme();
  const [editing, setEditing] = useImmer(false);
  const [draft, setDraft] = useImmer(description);

  useEffect(() => {
    if (!editing) {
      setDraft(description);
    }
  }, [description, editing, setDraft]);

  const trimmedDraft = draft.trim();

  const containerSx =
    chrome === "strip"
      ? {
          bgcolor: theme.igBackgroundColors.baseLightHigh,
          borderBottom: `1px solid ${theme.igBorders.core.default}`,
          px: 2,
          py: 1.5,
        }
      : { py: 0 };

  const handleSave = async () => {
    if (!trimmedDraft || trimmedDraft === description) {
      return;
    }
    await onSave(trimmedDraft);
    setEditing(false);
  };

  if (editing) {
    return (
      <Stack spacing={1} sx={containerSx}>
        <IgTextField
          label="Rule description"
          fullWidth
          multiline
          maxRows={4}
          slotProps={{ htmlInput: { maxLength: 1024 } }}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <IgButton
            size="mini"
            variant="text"
            onClick={() => {
              setDraft(description);
              setEditing(false);
            }}
          >
            Cancel
          </IgButton>
          <IgButton
            size="mini"
            variant="contained"
            loading={saving}
            disabled={!trimmedDraft || trimmedDraft === description}
            onClick={() => void handleSave()}
          >
            Save description
          </IgButton>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{
        ...(chrome === "strip" ? { ...containerSx, py: 1.25 } : containerSx),
        "&:hover .RuleDescriptionEditor-pencil, &:focus-within .RuleDescriptionEditor-pencil":
          {
            opacity: 1,
          },
      }}
    >
      <IgTypography
        // The rule header's summary is Text/Core/Secondary.
        color={
          chrome === "inline" ? IgCoreTextColors.secondary : "text.secondary"
        }
        variant="body2"
        sx={{ flex: 1 }}
      >
        {description}
      </IgTypography>
      {!readOnly ? (
        <IgIconButton
          className="RuleDescriptionEditor-pencil"
          aria-label="Edit rule description"
          size="small"
          onClick={() => setEditing(true)}
          sx={{ opacity: 0, transition: "opacity 120ms ease" }}
        >
          <Pencil size={15} />
        </IgIconButton>
      ) : null}
    </Stack>
  );
};
